/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import useCustomMessageError from 'src/themes/shared/containers/Player/hooks/useCustomMessageError';
import useLoadUserPreferences from 'src/themes/shared/containers/Player/hooks/useLoadUserPreferences';
import usePlayerIntervals from 'src/themes/shared/containers/Player/hooks/usePlayerIntervals';
import PlayerMarkup from 'src/themes/shared/components/PlayerMarkup';
import Poster from 'src/themes/shared/components/Poster';
import usePlayback from 'src/hooks/usePlayback';
import { useSelector } from 'react-redux';
import { SELECTOR_PLAYBACK_FIRST_PLAY } from 'src/store/reducers/playback';
import usePlayerError from 'src/hooks/usePlayerError';
import useStats from 'src/hooks/useStats';
import useUI from 'src/hooks/useUI';
import { detectAndroid, detectIOS } from 'src/utils/browserUtils';
import useTranslation from 'next-translate/useTranslation';
import useTextTrack from 'src/themes/shared/containers/Player/hooks/useTextTrack';
import useSubtitle from 'src/hooks/useSubtitle';
import LoadingSpinner from 'src/themes/shared/components/LoadingSpinner';
import IgnorePlayerEvents from 'src/themes/shared/components/IgnorePlayerEvents';
import classNames from 'classnames';
import useAutoplay from 'src/hooks/useAutoplay';
import useApplicationData from 'src/hooks/useApplicationData';
import { MEDIA_TYPE } from 'src/interfaces/enums';
import useMarker from 'src/hooks/useMarker';
import BigPlayButton from 'src/themes/cosmos/components/BigPlayButton';
import OverlayError from 'src/themes/cosmos/components/OverlayError';
import UnmuteButton from 'src/themes/cosmos/components/UnmuteButton';
import SubtitleText from 'src/themes/cosmos/components/SubtitleText';
import AutoplayOverlay from 'src/themes/cosmos/containers/AutoplayOverlay';
import Chip from 'src/themes/cosmos/components/Chip';
import NotesContainer from 'src/themes/cosmos/containers/NotesContainer';
import useContextMenu from 'src/hooks/useContextMenu';
import { UserEventTriggerType } from 'src/interfaces/Datahub';
import PlayerContextMenu from '../PlayerContextMenu';
import PlayerActiveOverlay from '../PlayerActiveOverlay';
import styles from './styles.module.scss';
import useActiveOverlay from './hooks/useActiveOverlay';

export interface IPlayerProps {
  posterImgUrl?: string;
}

const Player = ({ posterImgUrl }: IPlayerProps) => {
  useLoadUserPreferences();
  usePlayerIntervals();
  useCustomMessageError();

  const { stats } = useStats();
  const { togglePlay, isMuted, unmuted, videoDimensions } = usePlayback();
  const { toggleFullscreen, toggleUserActive } = useUI();
  const applicationData = useApplicationData();
  const { mediaType, platformUserId, flags } = applicationData;
  const { error } = usePlayerError();
  const { t } = useTranslation('common');
  const { subtitleEnabled, subtitleStyle } = useSubtitle();
  const { currentCueText } = useTextTrack();
  const { isAutoplayVisible, hideAutoplay } = useAutoplay();
  const { isContextMenuVisible } = useContextMenu();

  const isAudio = mediaType === MEDIA_TYPE.audio;
  const firstPlay = useSelector(SELECTOR_PLAYBACK_FIRST_PLAY);
  const isMobile = detectAndroid() || detectIOS();
  const isVideoLandscape = videoDimensions.width >= videoDimensions.height;

  const { isActiveOverlayVisible } = useActiveOverlay();

  const isUserIdVisible = !isActiveOverlayVisible && platformUserId && flags.ENABLE_PLAYER_DISPLAY_USER_ID;
  const { isMarkerOverlayVisible: isSideNotesEnabled, hideMarkerOverlay } = useMarker();

  const isSideNotesVisible = isSideNotesEnabled && flags.ENABLE_PLAYER_COSMOS_NOTES && !isMobile;
  const shouldDisplayPoster = !firstPlay || applicationData.mediaType === MEDIA_TYPE.audio;

  return (
    <div
      role='main'
      className={classNames(styles.container, {
        [styles.hideNativeSubtitles]: !detectIOS(),
        [styles.sideNotesVisible]: isSideNotesVisible,
        [styles.forceLandscape]: isVideoLandscape && isMobile,
      })}
    >
      <UnmuteButton visible={isMuted && firstPlay} onClick={() => unmuted(UserEventTriggerType.CLICK)} />
      <div
        data-vjs-player
        data-resolution={stats.media.mediaResolution}
        onClick={() => {
          if (isMuted && firstPlay) unmuted(UserEventTriggerType.CLICK);
          else togglePlay(UserEventTriggerType.CLICK);
        }}
        onTouchStart={() => {
          toggleUserActive();
          if (isMuted && firstPlay) {
            unmuted(UserEventTriggerType.CLICK);
          }
        }}
        onDoubleClick={() => toggleFullscreen(UserEventTriggerType.CLICK)}
      >
        <PlayerMarkup applicationData={applicationData} className={styles.content} />

        {isUserIdVisible && (
          <Chip
            className={styles.platformUserId}
            text={t('general.userId', {
              id: platformUserId,
            })}
          />
        )}

        {error && <OverlayError error={error} />}

        <IgnorePlayerEvents>
          {!firstPlay && <BigPlayButton onClick={() => togglePlay(UserEventTriggerType.CLICK)} />}
          {isAutoplayVisible && <AutoplayOverlay onHide={hideAutoplay} />}
          {isContextMenuVisible && <PlayerContextMenu />}
        </IgnorePlayerEvents>

        {shouldDisplayPoster && posterImgUrl && <Poster src={posterImgUrl} />}

        <PlayerActiveOverlay />

        <LoadingSpinner isAudio={isAudio} />

        {subtitleEnabled && currentCueText && !detectIOS() && (
          <SubtitleText
            color={subtitleStyle.color}
            size={subtitleStyle.size}
            background={subtitleStyle.background}
            stroke={subtitleStyle.stroke}
            opacity={subtitleStyle.opacity}
            className={classNames(styles.subtitle, {
              [styles.sideNotesVisible]: isSideNotesVisible,
              [styles.bottomBarOffset]: isActiveOverlayVisible,
              [styles.offsetMobile]: isMobile,
            })}
            currentText={currentCueText}
          />
        )}
      </div>
      {isSideNotesVisible && <NotesContainer onHide={hideMarkerOverlay} />}
    </div>
  );
};

export default Player;
